// Vertical step timeline: what the backend is doing for this turn, one row per
// stage (pending → active → done), with strategies / agent tool calls fanned
// out under the search stage. Rows that carry more detail expand in place;
// the retrieval card passes its deep-dive (plan, thresholds, …) per stage.
// Timed turns add where the time went: a duration bar per row (LLM rows in
// their own colour, with a model chip; remote services with a chip) and the
// answer's split into waiting for the first token and streaming.
import React, { useEffect, useRef, useState } from 'react';
import type { AnswerSplit, RowStatus, StageKey, Timeline, TimelineStage, TimelineSubRow } from './timelineModel';
import { formatSeconds } from './timelineModel';
import { STRATEGY_TOOLTIPS, displayName, familyOf } from './strategies';

/** Wall-clock seconds, re-rendering every `ms` while `active`. */
export function useNowSeconds(active: boolean, ms = 200): number {
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    if (!active) return;
    setNow(Date.now() / 1000);
    const id = setInterval(() => setNow(Date.now() / 1000), ms);
    return () => clearInterval(id);
  }, [active, ms]);
  return active ? now : Date.now() / 1000;
}

export function StepIcon({ status, small = false }: { status: RowStatus; small?: boolean }) {
  const cls = `malpedia-step-icon malpedia-step-icon--${status}${small ? ' malpedia-step-icon--small' : ''}`;
  if (status === 'done') {
    return (
      <svg className={cls} viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" className="malpedia-step-icon-fill" />
        <path d="M4.7 8.3 L7 10.5 L11.3 5.9" className="malpedia-step-icon-check" />
      </svg>
    );
  }
  if (status === 'active') {
    return (
      <svg className={cls} viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6.25" className="malpedia-step-icon-track" />
        <path d="M8 1.75 A6.25 6.25 0 0 1 14.25 8" className="malpedia-step-icon-arc" />
        <circle cx="8" cy="8" r="2" className="malpedia-step-icon-core" />
      </svg>
    );
  }
  if (status === 'error') {
    return (
      <svg className={cls} viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="7" className="malpedia-step-icon-fill" />
        <path d="M8 4.5 V9" className="malpedia-step-icon-check" />
        <circle cx="8" cy="11.4" r="0.9" className="malpedia-step-icon-dot" />
      </svg>
    );
  }
  if (status === 'skipped') {
    return (
      <svg className={cls} viewBox="0 0 16 16" aria-hidden="true">
        <path d="M5 8 H11" className="malpedia-step-icon-dash" />
      </svg>
    );
  }
  return (
    <svg className={cls} viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" className="malpedia-step-icon-ring" />
    </svg>
  );
}

const STATUS_TEXT: Record<RowStatus, string> = {
  pending: 'pending',
  active: 'running',
  done: 'done',
  skipped: 'no result reported',
  error: 'failed',
};

const THINKING_TAIL_CHARS = 160;

/** Faint, right-anchored tail of the planner's streamed reasoning tokens. */
function ThinkingTail({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const tail = text.slice(-THINKING_TAIL_CHARS).replace(/\s+/g, ' ');
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [tail]);
  return (
    <div className="malpedia-step-thinking" ref={ref} aria-hidden="true">
      <span>{tail}</span>
    </div>
  );
}

function countText(count: number | undefined, unit: string | undefined): string {
  if (count == null) return '';
  return unit ? `${count.toLocaleString()} ${unit}` : count.toLocaleString();
}

function SubRow({ row, unitFallback }: { row: TimelineSubRow; unitFallback: string }) {
  const name = row.name ? displayName(row.name) : '';
  const result =
    !row.name ? '' // the agent's "choosing next step" placeholder
    : row.status === 'active' ? 'running…'
    : row.status === 'error' ? 'error'
    : row.status === 'skipped' ? '—'
    : countText(row.count, row.unit ?? (row.count != null ? unitFallback : undefined));
  return (
    <li
      className="malpedia-step-sub"
      data-status={row.status}
      title={row.title || (row.name ? STRATEGY_TOOLTIPS[row.name] : undefined)}
    >
      <StepIcon status={row.status} small />
      {row.name && (
        <span
          className="malpedia-step-swatch"
          data-strategy={row.name}
          data-family={familyOf(row.name)}
          aria-hidden="true"
        />
      )}
      {name && <span className="malpedia-step-sub-name">{name}</span>}
      {row.target && <span className="malpedia-step-sub-target">{row.target}</span>}
      {row.llmS != null && (
        <span className="malpedia-step-chip" data-kind="llm" title={row.model ? `LLM call · ${row.model}` : 'LLM call'}>
          LLM {formatSeconds(row.llmS)}
        </span>
      )}
      <span className="malpedia-step-sub-result" key={row.status}>
        {result}
        <span className="malpedia-sr-only"> ({STATUS_TEXT[row.status]})</span>
      </span>
    </li>
  );
}

/** Seconds a row has taken so far (live for the active row). */
function rowSeconds(stage: TimelineStage, now: number): number | undefined {
  if (stage.status === 'active' && stage.startTs != null) return Math.max(0, now - stage.startTs);
  return stage.durationS;
}

/** "LLM · GLM-5.3", "gpufkie", "CPU fallback" — where the row's time went. */
function KindChip({ stage }: { stage: TimelineStage }) {
  if (stage.kind === 'llm') {
    return (
      <span className="malpedia-step-chip" data-kind="llm" title="Time spent waiting for the language model">
        LLM{stage.model ? ` · ${stage.model}` : ''}
      </span>
    );
  }
  if (stage.service === 'fallback') {
    return (
      <span className="malpedia-step-chip" data-kind="fallback" title="Remote service unavailable — served by the local CPU copy">
        CPU fallback
      </span>
    );
  }
  if (stage.kind === 'remote') {
    return (
      <span className="malpedia-step-chip" data-kind="remote" title="Served by a remote GPU service">
        {stage.service || 'remote'}
      </span>
    );
  }
  return null;
}

/** Waiting for the first token, then streaming — under "Writing answer". */
function AnswerSplitLines({
  answer, stage, now, answerChars,
}: { answer: AnswerSplit; stage: TimelineStage; now: number; answerChars?: number }) {
  const isActive = stage.status === 'active';
  const firstTokenTs =
    answer.firstTokenTs ??
    (answer.ttftS != null && stage.startTs != null ? stage.startTs + answer.ttftS : undefined);
  const waitS =
    answer.ttftS ??
    (firstTokenTs != null && stage.startTs != null ? firstTokenTs - stage.startTs : undefined);
  const streaming = firstTokenTs != null || (answerChars ?? 0) > 0;

  let wait: string | null = null;
  if (waitS != null) wait = formatSeconds(Math.max(0, waitS));
  else if (isActive && !streaming && stage.startTs != null) wait = `${formatSeconds(Math.max(0, now - stage.startTs))}…`;

  let stream: string | null = null;
  if (answer.done && answer.tokens != null) {
    stream = `${answer.tokensEstimated ? '≈' : ''}${answer.tokens.toLocaleString()} tok`;
    if (answer.tokS != null) stream += ` · ${answer.tokS.toFixed(answer.tokS < 10 ? 1 : 0)} tok/s`;
  } else if (isActive && streaming) {
    // Live: ~4 characters per token until the server reports the count.
    const est = Math.round((answerChars ?? 0) / 4);
    stream = `≈${est.toLocaleString()} tok`;
    const secs = firstTokenTs != null ? now - firstTokenTs : 0;
    if (secs >= 1 && est > 0) stream += ` · ${(est / secs).toFixed(est / secs < 10 ? 1 : 0)} tok/s`;
  }
  if (!wait && !stream) return null;
  return (
    <ul className="malpedia-step-split">
      {wait && (
        <li data-live={isActive && waitS == null ? 'true' : undefined}>
          <span>waiting for first token</span>
          <span className="malpedia-step-split-value">{wait}</span>
        </li>
      )}
      {stream && (
        <li data-live={isActive ? 'true' : undefined}>
          <span>streaming</span>
          <span className="malpedia-step-split-value">{stream}</span>
        </li>
      )}
    </ul>
  );
}

interface StepRowProps {
  stage: TimelineStage;
  index: number;
  now: number;
  planningTokens?: string;
  detail?: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** Bar length as a share of the turn's total (timed turns only). */
  barShare?: number;
  answerChars?: number;
}

function StepRow({ stage, index, now, planningTokens, detail, expanded, onToggle, barShare, answerChars }: StepRowProps) {
  const isActive = stage.status === 'active';
  const elapsed = rowSeconds(stage, now);
  const time = elapsed != null ? formatSeconds(elapsed) : '';
  const count = countText(stage.count, stage.unit);
  const lastMessage = stage.messages[stage.messages.length - 1];
  const showThinking = isActive && stage.key === 'plan' && !!planningTokens;
  const showSplit = !!stage.answer && (isActive || stage.status === 'done');
  const showLiveMessage =
    isActive && !showThinking && !showSplit && stage.subRows.length === 0 && !!lastMessage;
  const expandable = !!detail || stage.messages.length > 0;
  const detailId = `malpedia-step-detail-${stage.key}-${index}`;

  const head = (
    <>
      <span className="malpedia-step-node">
        <StepIcon status={stage.status} />
      </span>
      <span className="malpedia-step-label">{stage.label}</span>
      {stage.sublabel && <span className="malpedia-step-sublabel">{stage.sublabel}</span>}
      <KindChip stage={stage} />
      <span className="malpedia-step-meta">
        {count && <span className="malpedia-step-count">{count}</span>}
        {barShare != null && (
          <span className="malpedia-step-bar" data-kind={stage.kind} aria-hidden="true">
            <span
              className="malpedia-step-bar-fill"
              style={{ width: `${Math.max(barShare > 0 ? 2 : 0, Math.min(100, barShare * 100))}%` }}
            />
          </span>
        )}
        {time && (
          <span className="malpedia-step-time" data-live={isActive ? 'true' : undefined}>
            {time}
          </span>
        )}
        <span className="malpedia-sr-only">{STATUS_TEXT[stage.status]}</span>
      </span>
      <span className="malpedia-step-chevron" aria-hidden="true">
        {expandable ? '▸' : ''}
      </span>
    </>
  );

  return (
    <li
      className="malpedia-step"
      data-status={stage.status}
      data-stage={stage.key}
      data-expanded={expanded ? 'true' : undefined}
      style={{ '--malpedia-step-i': index } as React.CSSProperties}
    >
      {expandable ? (
        <button
          type="button"
          className="malpedia-step-head"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailId}
        >
          {head}
        </button>
      ) : (
        <div className="malpedia-step-head">{head}</div>
      )}

      {showThinking && <ThinkingTail text={planningTokens!} />}
      {showLiveMessage && <div className="malpedia-step-live-msg">{lastMessage}</div>}
      {showSplit && <AnswerSplitLines answer={stage.answer!} stage={stage} now={now} answerChars={answerChars} />}

      {stage.subRows.length > 0 && (
        <ul className="malpedia-step-subs">
          {stage.subRows.map((r) => (
            <SubRow key={r.id} row={r} unitFallback={stage.unit ?? ''} />
          ))}
        </ul>
      )}

      {expandable && (
        <div className={`malpedia-step-collapse${expanded ? ' open' : ''}`} id={detailId}>
          <div className="malpedia-step-collapse-inner">
            <div className="malpedia-step-detail">
              {stage.messages.length > 0 && (
                <ul className="malpedia-step-messages">
                  {stage.messages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              )}
              {detail}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

interface StepTimelineProps {
  timeline: Timeline;
  planningTokens?: string;
  /** Deep-dive content shown when a stage row is expanded. */
  details?: Partial<Record<StageKey, React.ReactNode>>;
  /** False while the timeline is folded away: stops the elapsed-time ticker. */
  visible?: boolean;
}

export function StepTimeline({ timeline, planningTokens, details, visible = true }: StepTimelineProps) {
  const now = useNowSeconds(visible && timeline.live && !!timeline.active);
  const [expanded, setExpanded] = useState<ReadonlySet<StageKey>>(() => new Set());

  if (timeline.stages.length === 0) return null;

  // Bars share the turn's total: the stages run one after another.
  const total = timeline.timed
    ? timeline.stages.reduce((n, s) => n + (rowSeconds(s, now) ?? 0), 0)
    : 0;
  const barShare = (s: TimelineStage): number | undefined =>
    timeline.timed && s.status !== 'pending' ? (total > 0 ? (rowSeconds(s, now) ?? 0) / total : 0) : undefined;

  const toggle = (key: StageKey) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <ol className="malpedia-steps" data-live={timeline.live ? 'true' : undefined} aria-label="Retrieval steps">
      {timeline.stages.map((s, i) => (
        <StepRow
          key={s.key}
          stage={s}
          index={i}
          now={now}
          planningTokens={planningTokens}
          detail={details?.[s.key]}
          expanded={expanded.has(s.key)}
          onToggle={() => toggle(s.key)}
          barShare={barShare(s)}
          answerChars={timeline.answerChars}
        />
      ))}
    </ol>
  );
}
